import { IFilter, Pagination } from '@isdd/idsk-ui-kit/types'
import { BASE_PAGE_NUMBER, BASE_PAGE_SIZE, KRIT_HODNOTENIE } from '@isdd/metais-common/api/constants'
import { mapFilterToNeighborsApi } from '@isdd/metais-common/api/filter/filterApi'
import {
    ConfigurationItemUi,
    NeighbourPairUi,
    useReadCiNeighbours,
    useReadConfigurationItem,
    useReadNeighboursConfigurationItems,
} from '@isdd/metais-common/api/generated/cmdb-swagger'
import { getGetIdentitiesByLoginsBulkQueryKey, useGetIdentitiesByLoginsBulk } from '@isdd/metais-common/api/generated/iam-swagger'
import { useAuth } from '@isdd/metais-common/contexts/auth/authContext'
import { useUserPreferences } from '@isdd/metais-common/contexts/userPreferences/userPreferencesContext'
import { useFilterParams } from '@isdd/metais-common/hooks/useFilter'
import uniq from 'lodash/uniq'
import React, { useEffect, useState } from 'react'
import { getGetDocumentQueryKey, useGetDocument, WikiDocument } from '@isdd/metais-common/api/generated/wiki-swagger'
import { ENTITY_KRIS } from '@isdd/metais-common/constants'
import { RowSelection, useRowSelection } from '@isdd/metais-common/hooks/useRowSelection'

import { mapNeighboursSetSourceToPagination } from '@/componentHelpers/pagination'

export interface TableCols extends NeighbourPairUi {
    selected?: boolean
}

export interface IView {
    ciData?: ConfigurationItemUi
    data?: NeighbourPairUi[]
    protocols?: ConfigurationItemUi[]
    pagination: Pagination
    handleFilterChange: (filter: IFilter) => void
    isLoading: boolean
    isError: boolean
    refetch: () => void
    namesData?: { login: string; fullName: string }[]
    rowSelection: RowSelection<TableCols>
    xWikiDocData?: WikiDocument
}

interface IKRISDocumentListContainer {
    configurationItemId?: string
    View: React.FC<IView>
}

export const defaultFilter = {
    sort: [],
    pageNumber: BASE_PAGE_NUMBER,
    pageSize: BASE_PAGE_SIZE,
    fullTextSearch: '',
}

export const KRISDocumentListContainer: React.FC<IKRISDocumentListContainer> = ({ configurationItemId, View }) => {
    const { currentPreferences } = useUserPreferences()
    const {
        state: { user, token },
    } = useAuth()

    const metaAttributes = currentPreferences.showInvalidatedItems ? { state: ['DRAFT', 'INVALIDATED'] } : { state: ['DRAFT'] }
    const { data: ciData, isLoading: isCiLoading } = useReadConfigurationItem(configurationItemId ?? '')
    const { data: xWikiDocData, fetchStatus: wikiDocFetchStatus } = useGetDocument(
        ENTITY_KRIS,
        ciData?.uuid ?? '',
        KRIT_HODNOTENIE,
        {},
        {
            query: {
                queryKey: getGetDocumentQueryKey(ENTITY_KRIS, ciData?.uuid ?? '', KRIT_HODNOTENIE),
                retry: 2,
                enabled: token !== null && !!user?.uuid,
            },
        },
    )

    const [defaultRequestApi, setDefaultRequestApi] = useState({
        neighboursFilter: {
            ciType: ['Dokument'],
            metaAttributes,
            relType: ['CI_HAS_DOCUMENT', 'Dokument_sa_tyka_KRIS', 'CONTROL_HAS_DOCUMENT', 'PROJECT_HAS_DOCUMENT'],
            usageType: ['system', 'application'],
            fullTextSearch: '',
        },
    })

    const { filter, handleFilterChange } = useFilterParams(defaultFilter)

    const {
        isLoading,
        isError,
        data: documentCiData,
        refetch,
    } = useReadCiNeighbours(configurationItemId ?? '', mapFilterToNeighborsApi(filter, defaultRequestApi), {})

    const { data: protocols, isLoading: isProtocolsLoading } = useReadNeighboursConfigurationItems(configurationItemId ?? '', {
        nodeType: 'KRIS_Protokol',
        relationshipType: 'KRIS_protokol_sa_tyka_KRIS',
    })

    const { data: userIdentitiesData } = useGetIdentitiesByLoginsBulk(
        uniq([
            ...(documentCiData?.fromNodes?.neighbourPairs?.map((item) => item.configurationItem?.metaAttributes?.createdBy ?? '') ?? []),
            ...(documentCiData?.fromNodes?.neighbourPairs?.map((item) => item.configurationItem?.metaAttributes?.lastModifiedBy ?? '') ?? []),
        ]),
        {
            query: {
                queryKey: getGetIdentitiesByLoginsBulkQueryKey(
                    uniq([
                        ...(documentCiData?.fromNodes?.neighbourPairs?.map((item) => item.configurationItem?.metaAttributes?.createdBy ?? '') ?? []),
                        ...(documentCiData?.fromNodes?.neighbourPairs?.map((item) => item.configurationItem?.metaAttributes?.lastModifiedBy ?? '') ??
                            []),
                    ]),
                ),
                enabled: documentCiData !== undefined && user !== null,
            },
        },
    )

    const namesData = userIdentitiesData?.map((item) => {
        return { login: item.input ?? '', fullName: (item.identity?.firstName ?? '') + ' ' + (item.identity?.lastName ?? '') }
    })

    useEffect(() => {
        if (defaultRequestApi.neighboursFilter.fullTextSearch !== filter.fullTextSearch) {
            setDefaultRequestApi({ neighboursFilter: { ...defaultRequestApi.neighboursFilter, fullTextSearch: filter.fullTextSearch } })
        }
    }, [defaultRequestApi.neighboursFilter, filter])

    const pagination = mapNeighboursSetSourceToPagination(filter, documentCiData)
    const rowSelection = useRowSelection<TableCols>()

    if (!configurationItemId)
        return (
            <View
                refetch={refetch}
                pagination={pagination}
                handleFilterChange={handleFilterChange}
                isLoading={false}
                isError
                rowSelection={rowSelection}
                xWikiDocData={xWikiDocData}
            />
        )
    return (
        <View
            ciData={ciData}
            namesData={namesData}
            protocols={protocols?.toCiSet}
            refetch={refetch}
            data={documentCiData?.fromNodes?.neighbourPairs}
            pagination={pagination}
            handleFilterChange={handleFilterChange}
            isLoading={isLoading || isCiLoading || isProtocolsLoading || wikiDocFetchStatus != 'idle'}
            isError={isError}
            rowSelection={rowSelection}
            xWikiDocData={xWikiDocData}
        />
    )
}
